import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useCreateJournalVoucher, useUpdateJournalVoucher, useJournalAction,
  useJournalVouchers, useJournalVoucherDetail,
} from '../hooks/useTransactions';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useChartOfAccountsList } from '../hooks/useChartOfAccounts';
import { getApiErrorMessage } from '@/shared/api/axios';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';
import { Eye, Pencil, Trash2, CheckCircle2, RotateCcw, Send, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { localToday } from '@/shared/utils/utils';

type Line = { accountId: string; description: string; debit: number; credit: number };
const blankLine = (): Line => ({ accountId: '', description: '', debit: 0, credit: 0 });
const initialForm = () => ({
  date: localToday(),
  reference: '',
  description: '',
  notes: '',
  reportingBasis: 'accrual',
  currency: 'INR',
  exchangeRate: 1,
  entries: [blankLine(), blankLine()],
});

const statuses = ['all', 'draft', 'pending_approval', 'approved', 'posted', 'rejected', 'reversed'];

// Posted / draft / cancelled must be distinguishable at a glance in a voucher
// register, so each state uses the accessible `-text` pair on its own tint.
function StatusBadge({ status }: { status: string }) {
  const tones: Record<string, 'neutral' | 'warning' | 'info' | 'success' | 'danger'> = {
    draft: 'neutral',
    pending_approval: 'warning',
    approved: 'info',
    posted: 'success',
    reversed: 'neutral',
    cancelled: 'neutral',
    rejected: 'danger',
  };
  return (
    <Badge tone={tones[status] ?? 'neutral'} className="border border-outline">
      {status.replace('_', ' ')}
    </Badge>
  );
}

export function JournalVoucherList() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(initialForm);

  const { data: journals, isLoading, isError, error, refetch } = useJournalVouchers({ status: statusFilter, search });
  const { data: detailJournal, isLoading: detailLoading } = useJournalVoucherDetail(viewingId);

  // Deep link from the Ledger Explorer (?journalId=) straight into this
  // voucher's detail modal, instead of leaving a ledger row as a dead end.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const journalId = searchParams.get('journalId');
    if (!journalId) return;
    setViewingId(journalId);
    const next = new URLSearchParams(searchParams);
    next.delete('journalId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const { data: accounts, isLoading: accountsLoading } = useChartOfAccountsList();
  
  const create = useCreateJournalVoucher();
  const update = useUpdateJournalVoucher();
  const action = useJournalAction();

  const accountOptions = (accounts || [])
    .filter((a) => a.isActive)
    .map((a) => ({ value: a._id, label: `${a.code} — ${a.name} (${a.type})` }));

  const debit = form.entries.reduce((sum, line) => sum + Number(line.debit || 0), 0);
  const credit = form.entries.reduce((sum, line) => sum + Number(line.credit || 0), 0);
  const balanced = debit > 0 && Math.round(debit * 100) === Math.round(credit * 100) && form.entries.every((line) => line.accountId && ((line.debit > 0) !== (line.credit > 0)));

  const handleEditOpen = (journal: any) => {
    setEditingId(journal._id);
    setForm({
      date: new Date(journal.date).toISOString().slice(0, 10),
      reference: journal.reference || '',
      description: journal.description || '',
      notes: journal.notes || '',
      reportingBasis: journal.reportingBasis || 'accrual',
      currency: journal.currency || 'INR',
      exchangeRate: journal.exchangeRate || 1,
      entries: (journal.entries || []).map((e: any) => ({
        accountId: typeof e.accountId === 'object' ? e.accountId._id : e.accountId,
        description: e.description || '',
        debit: Number(e.debit || 0),
        credit: Number(e.credit || 0),
      })),
    });
    setIsCreating(true);
  };

  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await update.mutateAsync({ id: editingId, payload: form });
        toast.success('Journal voucher updated.');
      } else {
        await create.mutateAsync(form);
        toast.success('Draft journal voucher created.');
      }
      setForm(initialForm());
      setEditingId(null);
      setIsCreating(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not save journal.'));
    }
  };

  const runAction = (id: string, next: 'submit' | 'approve' | 'reject' | 'post' | 'reverse' | 'delete') => {
    let payload: any = {};
    if (next === 'reject' || next === 'reverse' || next === 'delete') {
      const reason = prompt(`Reason for ${next}:`);
      if (!reason && next !== 'delete') return;
      if (reason) payload = { reason };
    }
    if ((next === 'post' || next === 'reverse' || next === 'delete') && !confirm(`Confirm journal ${next}? This action is audited.`)) return;
    
    action.mutate(
      { id, action: next, payload },
      {
        onSuccess: () => toast.success(`Journal ${next} successful.`),
        onError: (error) => toast.error(getApiErrorMessage(error, `Journal ${next} failed.`)),
      }
    );
  };

  if (isLoading) return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <Skeleton className="h-16 w-full rounded-card" />
      <Skeleton className="h-10 w-full rounded-card-sm" />
      <Skeleton className="h-[360px] w-full rounded-card" />
    </div>
  );

  return (
    <>
      <Card className="shadow-xs h-full flex flex-col overflow-hidden">
        <CardHeader
          title={
            <div className="flex items-center gap-2">
              <h2 className="text-headline-sm font-semibold">Manual Journals</h2>
              <span className="rounded-card-sm px-2 py-0.5 font-mono tabular-nums text-label-sm bg-muted text-on-surface-variant">
                {journals?.length || 0}
              </span>
            </div>
          }
          subtitle="Journal vouchers, contra entries and adjustments posted by hand"
          action={
            <Button
              size="sm"
              onClick={() => {
                setEditingId(null);
                setForm(initialForm());
                setIsCreating(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" /> New Journal
            </Button>
          }
        />
        
        <CardBody className="space-y-4 p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative min-w-0 max-w-md flex-1 basis-full sm:basis-auto">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search journal vouchers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search voucher number, reference or notes"
                className="pl-9"
              />
            </div>
            <label className="sr-only" htmlFor="journal-status-filter">Filter by status</label>
            <select
              id="journal-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="h-10 rounded-lg border border-input bg-background px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {statuses.map((val) => (
                <option key={val} value={val}>
                  {val === 'all' ? 'All statuses' : val.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>

          {isError ? (
            <div role="alert" className="rounded-card border border-danger/20 bg-danger/10 p-4 text-sm text-danger-text">
              {getApiErrorMessage(error, 'Could not load journal vouchers.')}{' '}
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-card-sm border border-border">
              <table className="w-full text-body-sm text-left border-collapse">
                <thead className="text-label-caps uppercase text-on-surface-variant bg-muted border-b border-border sticky top-0">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Number</th>
                    <th className="p-3">Reference / Description</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {!journals?.length && (
                    <tr><td colSpan={6}><EmptyState title="No journal vouchers" description={search || statusFilter !== 'all' ? 'No voucher matches this search and status. Clear the filters to see the full register.' : 'Post a manual journal to record an adjustment that has no source document.'} /></td></tr>
                  )}
                  {journals?.map((journal) => (
                    <tr
                      key={journal._id}
                      className="hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => setViewingId(journal._id)}
                    >
                      <td className="p-3 whitespace-nowrap font-mono tabular-nums font-medium">
                        {new Date(journal.date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="p-3 font-mono tabular-nums font-semibold text-primary">
                        {journal.voucherNumber || 'Draft'}
                      </td>
                      <td className="p-3">
                        <p className="font-semibold text-foreground">{journal.reference || '—'}</p>
                        <p className="max-w-md truncate text-muted-foreground">{journal.description}</p>
                        {journal.rejectionReason && (
                          <p className="mt-0.5 text-xs font-medium text-danger-text">
                            Rejected: {journal.rejectionReason}
                          </p>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono tabular-nums font-medium text-on-surface">
                        {formatINR(journal.totalAmount)}
                      </td>
                      <td className="p-3">
                        <StatusBadge status={journal.status} />
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View journal details"
                            aria-label={`View journal ${journal.voucherNumber || 'draft'}`}
                            onClick={() => setViewingId(journal._id)}
                            className="h-7 w-7 p-0 text-on-surface-variant hover:bg-muted"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>

                          {['draft', 'rejected'].includes(journal.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit journal"
                              aria-label={`Edit journal ${journal.voucherNumber || 'draft'}`}
                              onClick={() => handleEditOpen(journal)}
                              className="h-7 w-7 p-0 text-primary hover:bg-primary-container"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          {['draft', 'rejected'].includes(journal.status) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runAction(journal._id, 'submit')}
                              className="h-7 px-2 text-xs font-semibold text-info-text border-info/25 hover:bg-info/10"
                            >
                              <Send className="h-3 w-3 mr-1" /> Submit
                            </Button>
                          )}

                          {journal.status === 'pending_approval' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runAction(journal._id, 'approve')}
                                className="h-7 px-2 text-xs font-semibold text-success-text border-success/25 hover:bg-success/10"
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => runAction(journal._id, 'reject')}
                                className="h-7 px-2 text-xs font-semibold text-destructive border-destructive/25 hover:bg-danger/10"
                              >
                                Reject
                              </Button>
                            </>
                          )}

                          {['draft', 'approved'].includes(journal.status) && (
                            <Button
                              size="sm"
                              onClick={() => runAction(journal._id, 'post')}
                              className="h-7 px-2 text-xs font-semibold"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Post
                            </Button>
                          )}

                          {journal.status === 'posted' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runAction(journal._id, 'reverse')}
                              className="h-7 px-2 text-xs font-semibold text-on-surface-variant border-outline hover:bg-muted"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" /> Reverse
                            </Button>
                          )}

                          {['draft', 'rejected'].includes(journal.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Delete journal"
                              aria-label={`Delete journal ${journal.voucherNumber || 'draft'}`}
                              onClick={() => runAction(journal._id, 'delete')}
                              className="h-7 w-7 p-0 text-destructive hover:bg-danger/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── VIEW JOURNAL DETAIL MODAL ─────────────────────────────────────── */}
      <Modal
        open={!!viewingId}
        onClose={() => setViewingId(null)}
        title={`Journal Voucher — ${detailJournal?.voucherNumber || 'Draft'}`}
        size="lg"
      >
        {detailLoading || !detailJournal ? (
          <Skeleton className="h-[300px]" />
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold">Date:</span>
                <p className="font-semibold">{new Date(detailJournal.date).toLocaleDateString('en-IN')}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold">Reference:</span>
                <p className="font-semibold">{detailJournal.reference || '—'}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold">Basis:</span>
                <p className="font-semibold capitalize">{detailJournal.reportingBasis || 'accrual'}</p>
              </div>
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold">Status:</span>
                <div>
                  <StatusBadge status={detailJournal.status} />
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs text-muted-foreground uppercase font-semibold">Description:</span>
              <p className="text-sm font-medium mt-0.5">{detailJournal.description}</p>
            </div>

            {detailJournal.notes && (
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold">Notes:</span>
                <p className="text-xs text-muted-foreground bg-muted/40 p-2 rounded">{detailJournal.notes}</p>
              </div>
            )}

            {/* Entries table — Dr and Cr share one figure treatment; the tint is the only difference. */}
            <div className="overflow-x-auto rounded-card-sm border border-border">
              <table className="w-full text-body-sm text-left">
                <thead className="bg-muted text-label-caps uppercase text-on-surface-variant border-b border-border">
                  <tr>
                    <th className="p-3">Account code & name</th>
                    <th className="p-3">Line description</th>
                    <th className="p-3 text-right">Debit</th>
                    <th className="p-3 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(detailJournal.entries || []).map((e: any, i: number) => {
                    const acc = typeof e.accountId === 'object' ? e.accountId : null;
                    return (
                      <tr key={i} className="hover:bg-muted/50">
                        <td className="p-3 font-semibold text-foreground">
                          {acc ? <><span className="font-mono tabular-nums">{acc.code}</span> — {acc.name}</> : e.accountId}
                        </td>
                        <td className="p-3 text-muted-foreground">{e.description || '—'}</td>
                        <td className="p-3 text-right font-mono tabular-nums font-medium text-on-surface">
                          {e.debit > 0 ? formatINR(e.debit) : '—'}
                        </td>
                        <td className="p-3 text-right font-mono tabular-nums font-medium text-on-surface">
                          {e.credit > 0 ? formatINR(e.credit) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/50">
                    <td colSpan={2} className="p-3 text-right">
                      <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Total</span>
                    </td>
                    <td className="p-3 text-right font-mono tabular-nums font-semibold text-on-surface">
                      {formatINR(detailJournal.totalAmount)}
                    </td>
                    <td className="p-3 text-right font-mono tabular-nums font-semibold text-on-surface">
                      {formatINR(detailJournal.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setViewingId(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── CREATE / EDIT JOURNAL MODAL ────────────────────────────────────── */}
      <Modal
        open={isCreating}
        onClose={() => {
          setIsCreating(false);
          setEditingId(null);
        }}
        title={editingId ? 'Edit Manual Journal' : 'Create Manual Journal'}
        size="lg"
      >
        <form className="space-y-4 pt-3" onSubmit={handleSaveForm}>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold uppercase text-muted-foreground">
              Date
              <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 text-sm" />
            </label>
            <label className="text-xs font-semibold uppercase text-muted-foreground">
              Reference
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Audit ref #JV-90" className="mt-1 text-sm" />
            </label>
          </div>

          <label className="block text-xs font-semibold uppercase text-muted-foreground">
            Description
            <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Main voucher purpose..." className="mt-1 text-sm" />
          </label>

          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs font-semibold uppercase text-muted-foreground">
              Basis
              <select value={form.reportingBasis} onChange={(e) => setForm({ ...form, reportingBasis: e.target.value })} className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm">
                <option value="accrual">Accrual</option>
                <option value="cash">Cash</option>
                <option value="both">Both</option>
              </select>
            </label>
            <label className="text-xs font-semibold uppercase text-muted-foreground">
              Currency
              <Input maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="mt-1 text-sm" />
            </label>
            <label className="text-xs font-semibold uppercase text-muted-foreground">
              Exchange Rate
              <Input type="number" min="0.00000001" step="any" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: Number(e.target.value) })} className="mt-1 text-sm" />
            </label>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-muted-foreground">Journal Entries (Debit & Credit)</label>
            {/* Column header so the two money inputs read as a ledger, not as four anonymous boxes. */}
            <div aria-hidden="true" className="grid grid-cols-[2fr_2fr_1fr_1fr_auto] gap-2 px-2.5 text-label-sm uppercase tracking-wider text-on-surface-variant">
              <span>Account</span>
              <span>Line note</span>
              <span className="text-right">Debit</span>
              <span className="text-right">Credit</span>
              <span className="w-9" />
            </div>
            {form.entries.map((line, index) => (
              <div key={index} className="grid grid-cols-[2fr_2fr_1fr_1fr_auto] gap-2 rounded-lg border border-border p-2.5 bg-slate-50/50 dark:bg-slate-900/50">
                <Select
                  required
                  placeholder={accountsLoading ? 'Loading…' : 'Select CoA Account'}
                  options={accountOptions}
                  value={line.accountId}
                  onChange={(e) => setForm({ ...form, entries: form.entries.map((item, i) => i === index ? { ...item, accountId: e.target.value } : item) })}
                />
                <Input
                  placeholder="Line note"
                  value={line.description}
                  onChange={(e) => setForm({ ...form, entries: form.entries.map((item, i) => i === index ? { ...item, description: e.target.value } : item) })}
                />
                <Input
                  aria-label={`Line ${index + 1} debit`}
                  placeholder="Debit"
                  type="number"
                  min="0"
                  step="0.01"
                  className="text-right font-mono tabular-nums text-success-text"
                  value={line.debit || ''}
                  onChange={(e) => setForm({ ...form, entries: form.entries.map((item, i) => i === index ? { ...item, debit: Number(e.target.value), credit: Number(e.target.value) > 0 ? 0 : item.credit } : item) })}
                />
                <Input
                  aria-label={`Line ${index + 1} credit`}
                  placeholder="Credit"
                  type="number"
                  min="0"
                  step="0.01"
                  className="text-right font-mono tabular-nums text-info-text"
                  value={line.credit || ''}
                  onChange={(e) => setForm({ ...form, entries: form.entries.map((item, i) => i === index ? { ...item, credit: Number(e.target.value), debit: Number(e.target.value) > 0 ? 0 : item.debit } : item) })}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove entry line ${index + 1}`}
                  disabled={form.entries.length <= 2}
                  className="h-9 w-9 p-0 text-destructive hover:bg-danger/10"
                  onClick={() => setForm({ ...form, entries: form.entries.filter((_, i) => i !== index) })}
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>

          <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, entries: [...form.entries, blankLine()] })}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add entry line
          </Button>

          <label className="block text-xs font-semibold uppercase text-muted-foreground">
            Notes / Internal Comments
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 block min-h-20 w-full rounded-md border border-input bg-background p-2.5 text-sm shadow-sm" />
          </label>

          <div className={`flex flex-wrap items-center justify-between gap-3 rounded-card-sm border p-3 text-sm font-semibold ${balanced ? 'border-success/25 bg-success/10 text-success-text' : 'border-warning/25 bg-warning/10 text-warning-text'}`}>
            <span className="font-mono tabular-nums">Total Debit: {formatINR(debit)}</span>
            <span className="font-mono tabular-nums">Total Credit: {formatINR(credit)}</span>
            <span className="font-mono tabular-nums">Difference: {formatINR(Math.abs(debit - credit))}</span>
            <span className="text-label-sm uppercase tracking-wider">{balanced ? 'Balanced' : 'Out of balance — posting is blocked'}</span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { setIsCreating(false); setEditingId(null); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={!balanced || create.isPending || update.isPending}>
              {editingId ? 'Save changes' : 'Save draft journal'}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
